'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import TrustBadge from './TrustBadge';
import { useLanguage } from '@/contexts/LanguageContext';

interface TrustItem {
  id: number;
  icon: string;
  title: { en: string; ar: string };
  description: { en: string; ar: string };
}

const TrustSection = () => {
  const { language, t } = useLanguage();
  
  const trustItems: TrustItem[] = [
    {
      id: 1,
      icon: "ShieldCheckIcon",
      title: { en: 'Fully Insured', ar: 'مؤمن بالكامل' },
      description: { en: 'Comprehensive insurance coverage for complete peace of mind during your move', ar: 'تغطية تأمينية شاملة لراحة بال كاملة أثناء نقلك' }
    },
    {
      id: 2,
      icon: "DocumentCheckIcon",
      title: { en: 'Licensed & Certified', ar: 'مرخص ومعتمد' },
      description: { en: 'Officially registered and certified moving company in Kuwait', ar: 'شركة نقل مسجلة ومعتمدة رسمياً في الكويت' }
    },
    {
      id: 3,
      icon: "ClockIcon",
      title: { en: '24/7 Availability', ar: 'متاح ٢٤/٧' },
      description: { en: 'Round-the-clock service to accommodate your schedule anytime', ar: 'خدمة على مدار الساعة لتناسب جدولك في أي وقت' }
    },
    {
      id: 4,
      icon: "UserGroupIcon",
      title: { en: 'Expert Team', ar: 'فريق خبير' },
      description: { en: 'Trained professionals with years of moving experience', ar: 'محترفون مدربون بسنوات من الخبرة في النقل' }
    },
    {
      id: 5,
      icon: "CurrencyDollarIcon",
      title: { en: 'Transparent Pricing', ar: 'تسعير شفاف' },
      description: { en: 'No hidden fees - clear, upfront pricing for all services', ar: 'لا رسوم مخفية - تسعير واضح ومقدم لجميع الخدمات' }
    },
    {
      id: 6,
      icon: "TruckIcon",
      title: { en: 'Modern Fleet', ar: 'أسطول حديث' },
      description: { en: 'Well-maintained vehicles equipped for safe transportation', ar: 'مركبات محافظ عليها جيداً ومجهزة للنقل الآمن' }
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-full mb-4">
            <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-body font-semibold text-primary">{t({ en: 'Why Choose Us', ar: 'لماذا تختارنا' })}</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
            {t({ en: 'Your Trust is Our Priority', ar: 'ثقتك هي أولويتنا' })}
          </h2>
          <p className="text-lg text-text-secondary font-body">
            {t({ en: 'We combine professional expertise with genuine care to deliver exceptional moving experiences across Kuwait.', ar: 'نجمع بين الخبرة المهنية والرعاية الحقيقية لتقديم تجارب نقل استثنائية في جميع أنحاء الكويت.' })}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {trustItems.map((item) => (
            <TrustBadge
              key={item.id}
              icon={item.icon}
              title={item.title[language]}
              description={item.description[language]}
            />
          ))}
        </div>
        
        <div className="mt-12 bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 lg:p-12 text-white text-center max-w-4xl mx-auto">
          <Icon name="CheckBadgeIcon" size={48} variant="solid" className="text-accent mx-auto mb-4" />
          <h3 className="text-2xl lg:text-3xl font-headline font-bold mb-4">
            {t({ en: 'Trusted by Over 5,000+ Customers', ar: 'موثوق به من قبل أكثر من ٥٠٠٠+ عميل' })}
          </h3>
          <p className="text-lg text-white/90 font-body mb-6">
            {t({ en: 'Join thousands of satisfied customers who chose Almanara for their moving needs in Kuwait.', ar: 'انضم إلى آلاف العملاء الراضين الذين اختاروا المنارة لاحتياجات النقل الخاصة بهم في الكويت.' })}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={24} variant="solid" className="text-accent" />
              <span className="font-headline font-bold text-2xl">4.9/5</span>
              <span className="text-white/80 font-body">{t({ en: 'Average Rating', ar: 'متوسط التقييم' })}</span>
            </div>
            <div className="hidden sm:block w-px h-8 bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <Icon name="UserGroupIcon" size={24} variant="solid" className="text-accent" />
              <span className="font-headline font-bold text-2xl">5,000+</span>
              <span className="text-white/80 font-body">{t({ en: 'Happy Customers', ar: 'عملاء سعداء' })}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;